import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRightLeft, Check } from 'lucide-react'
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
} from '@ludus/ui'
import { asStatusId, type InternalStatus, type Lead, type PipelineId } from '@ludus/core'
import { updateLead } from '@/lib/api/pipelines.ts'
import { asApiError, editErrorMessage } from '../utils/editError.ts'

interface MoveStageDialogProps {
  lead: Lead
  statuses: InternalStatus[]
  pipelineId: PipelineId
  onSaved: (lead: Lead) => void
}

/**
 * The hero "Move stage" action (SBO-57). Opens a modal with the pipeline's
 * stages ordered by position; picking one + confirming PATCHes `statusId`.
 * This is the single stage editor — the Properties panel's Stage row is
 * display-only by design.
 */
export function MoveStageDialog({ lead, statuses, pipelineId, onSaved }: MoveStageDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState(lead.statusId as string)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Re-sync the selection to the lead's current stage each time the dialog opens.
  useEffect(() => {
    if (open) {
      setSelected(lead.statusId as string)
      setError(null)
    }
  }, [open, lead.statusId])

  const ordered = [...statuses].sort((a, b) => a.position - b.position)
  const changed = selected !== (lead.statusId as string)

  // The footer button is disabled while `!changed`, so confirm always has a
  // real stage change to persist.
  async function confirm() {
    setSaving(true)
    setError(null)
    try {
      const updated = await updateLead(pipelineId, lead.id, { statusId: asStatusId(selected) })
      onSaved(updated)
      setOpen(false)
    } catch (e) {
      setError(editErrorMessage(asApiError(e), t))
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Button variant="primary" size="sm" onClick={() => setOpen(true)}>
        <ArrowRightLeft className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
        {t('pages.leads.detail.actions.moveStage', { defaultValue: 'Move stage' })}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="min-w-0">
              <DialogTitle>
                {t('pages.leads.detail.moveDialog.title', { defaultValue: 'Move to stage' })}
              </DialogTitle>
              <DialogDescription>
                {t('pages.leads.detail.moveDialog.description', {
                  defaultValue: 'Pick the stage this lead should move to.',
                })}
              </DialogDescription>
            </div>
            <DialogClose />
          </DialogHeader>
          <DialogBody className="py-4">
            <ul className="flex flex-col gap-1.5" role="radiogroup">
              {ordered.map((s) => {
                const isSelected = s.id === selected
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => setSelected(s.id as string)}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                        isSelected
                          ? 'border-primary/50 bg-primary/15 text-foreground'
                          : 'border-border bg-background-secondary/40 text-foreground/85 hover:border-border-hover',
                      )}
                    >
                      <span className="min-w-0 truncate">{s.name}</span>
                      {isSelected && (
                        <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
            {error && (
              <p role="alert" className="mt-3 text-[11px] font-medium text-toast-error-text">
                {error}
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={saving}
            >
              {t('pages.leads.detail.moveDialog.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={confirm}
              disabled={saving || !changed}
            >
              {t('pages.leads.detail.moveDialog.confirm', { defaultValue: 'Move' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
